const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export { API_URL };

/** Get the auth token stored after login/signup. */
function getAuthToken(): string {
  try {
    const stored = localStorage.getItem('auth');
    if (stored) return JSON.parse(stored).token || '';
  } catch { /* */ }
  return '';
}

/** Headers with Authorization token attached. */
function authHeaders(): Record<string, string> {
  const token = getAuthToken();
  return token ? { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
               : { 'Content-Type': 'application/json' };
}

export const apiService = {
  /**
   * Start the pipeline as a background job on the server.
   * Returns immediately — pipeline keeps running even if the tab is closed.
   * Call pollPipelineStatus() to check for completion.
   */
  async startWorkflowBackground(): Promise<{ workflowId: string; pollUrl: string }> {
    const startResponse = await fetch(`${API_URL}/api/workflow/start`, {
      method: 'POST',
      headers: authHeaders(),
    });
    if (!startResponse.ok) throw new Error(`Failed to start workflow: ${startResponse.status}`);
    const startData = await startResponse.json();
    const workflowId = startData.workflow_id;

    // Fire background task — returns immediately with job_status: "started"
    const runResponse = await fetch(`${API_URL}/api/workflow/${workflowId}/run`, {
      method: 'POST',
      headers: authHeaders(),
    });
    if (!runResponse.ok) {
      let detail = `${runResponse.status}`;
      try {
        const errBody = await runResponse.json();
        // Surface pipeline_locked with the user-friendly message
        if (errBody.error === 'pipeline_locked') {
          throw new Error(errBody.message || 'Pipeline is currently running by another user');
        }
        if (errBody.error) detail = errBody.error;
        if (errBody.message) detail = errBody.message;
      } catch (inner) {
        if ((inner as Error).message?.includes('pipeline')) throw inner;
      }
      throw new Error(`Failed to start pipeline: ${detail}`);
    }
    const runData = await runResponse.json();
    return { workflowId, pollUrl: runData.poll_url };
  },

  /**
   * Poll the background pipeline status.
   * job_status: "running" | "done" | "error" | "not_started"
   */
  async pollPipelineStatus(workflowId: string): Promise<any> {
    const response = await fetch(`${API_URL}/api/workflow/${workflowId}/run/status`);
    if (!response.ok) throw new Error(`Poll failed: ${response.status}`);
    return await response.json();
  },

  /**
   * Start pipeline and wait for completion by polling every 5 seconds.
   * Safe to call from any component — works even if the user navigates away
   * and comes back, because the pipeline runs server-side.
   *
   * onProgress(status) is called on each poll with the current job_status.
   */
  async runWorkflow(onProgress?: (status: string) => void): Promise<any> {
    const { workflowId } = await this.startWorkflowBackground();

    // Poll until done or error
    const POLL_INTERVAL_MS = 5000;
    const MAX_WAIT_MS = 20 * 60 * 1000; // 20 min hard cap
    const started = Date.now();

    while (Date.now() - started < MAX_WAIT_MS) {
      await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS));
      const statusData = await this.pollPipelineStatus(workflowId);
      onProgress?.(statusData.job_status);

      if (statusData.job_status === 'done') return statusData;
      if (statusData.job_status === 'error') {
        throw new Error(`Pipeline failed: ${statusData.error || 'unknown error'}`);
      }
      // still "running" — continue polling
    }
    throw new Error('Pipeline timed out after 20 minutes');
  },

  async getArticles() {
    const response = await fetch(`${API_URL}/api/articles`);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  },

  async getArticle(articleId: string) {
    const response = await fetch(`${API_URL}/api/articles/${articleId}`);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  },

  async getNews() {
    const response = await fetch(`${API_URL}/api/news`);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  },

  async reviseArticle(articleId: string, feedback: string, workflowId: string = 'workflow_0') {
    const url = new URL(`${API_URL}/api/articles/${articleId}/revise`);
    url.searchParams.append('feedback', feedback);
    url.searchParams.append('workflow_id', workflowId);
    const response = await fetch(url.toString(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      let msg = `HTTP ${response.status}`;
      try { const body = await response.json(); if (body.message) msg = body.message; else if (body.error) msg = body.error; } catch {}
      throw new Error(msg);
    }
    return await response.json();
  },

  async deleteArticle(articleId: string) {
    const response = await fetch(`${API_URL}/api/articles/${articleId}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      let msg = `HTTP ${response.status}`;
      try { const body = await response.json(); if (body.error) msg = body.error; } catch {}
      throw new Error(msg);
    }
    return await response.json();
  },

  async publishArticle(articleId: string) {
    const response = await fetch(`${API_URL}/api/articles/${articleId}/publish`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) throw new Error('Failed to publish');
    return await response.json();
  },

  async getPlagiarismAnalysis(articleId: string) {
    const response = await fetch(`${API_URL}/api/articles/${articleId}/plagiarism`);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return await response.json();
  },

  async getStyleAnalysis(articleId: string) {
    const response = await fetch(`${API_URL}/api/articles/${articleId}/style-analysis`);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return await response.json();
  },

  async getArticleHistory(articleId: string, workflowId: string = 'workflow_0') {
    const url = new URL(`${API_URL}/api/articles/${articleId}/history`);
    url.searchParams.append('workflow_id', workflowId);
    const response = await fetch(url.toString());
    if (!response.ok) {
      let msg = `HTTP ${response.status}`;
      try { const b = await response.json(); if (b.error) msg = b.error; } catch {}
      throw new Error(msg);
    }
    return await response.json();
  },

  async getSources() {
    const response = await fetch(`${API_URL}/api/sources`);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data = await response.json();
    return data.sources || [];
  },

  async addSource(name: string, url: string, category: string) {
    const response = await fetch(`${API_URL}/api/sources`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, url, category }),
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return await response.json();
  },

  async removeSource(url: string, is_custom: boolean) {
    const response = await fetch(`${API_URL}/api/sources`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url, is_custom }),
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return await response.json();
  },

  async toggleSource(url: string, is_custom: boolean, enabled: boolean) {
    const response = await fetch(`${API_URL}/api/sources/toggle`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url, is_custom, enabled }),
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return await response.json();
  },

  async regenerateMissingImages() {
    const response = await fetch(`${API_URL}/api/articles/regenerate-missing-images`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  },

  async discoverNews() {
    const response = await fetch(`${API_URL}/api/discover`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) throw new Error(`Discovery failed: ${response.status}`);
    return await response.json();
  },

  discoverNewsStream(onBatch: (items: any[], totalSoFar: number, timestamp: string) => void, onDone: (total: number) => void, onError: (err: string) => void) {
    const eventSource = new EventSource(`${API_URL}/api/discover/stream`);
    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'batch') {
          onBatch(data.items, data.total_so_far, data.timestamp);
        } else if (data.type === 'done') {
          onDone(data.total);
          eventSource.close();
        }
      } catch (e) {
        console.error('SSE parse error:', e);
      }
    };
    eventSource.onerror = () => {
      onError('Connection lost during discovery');
      eventSource.close();
    };
    return eventSource;
  },

  async getActivityLogs(): Promise<{ total: number; events: any[] }> {
    const response = await fetch(`${API_URL}/api/logs`);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return await response.json();
  },

  async getGlobalPipelineStatus(): Promise<{ active: boolean; started_by?: string; workflow_id?: string; started_at?: string }> {
    const response = await fetch(`${API_URL}/api/pipeline/status`);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return await response.json();
  },

  async getDiscoveredNews() {
    const response = await fetch(`${API_URL}/api/discovered-news`);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return await response.json();
  },

  async deleteDiscoveredNewsItem(id: string) {
    const response = await fetch(`${API_URL}/api/discovered-news/${encodeURIComponent(id)}`, { method: 'DELETE' });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return await response.json();
  },

  async clearDiscoveredNews() {
    const response = await fetch(`${API_URL}/api/discovered-news`, { method: 'DELETE' });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return await response.json();
  },

  async generateSelected(ids: string[]) {
    const response = await fetch(`${API_URL}/api/generate-selected`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids }),
    });
    if (!response.ok) {
      let msg = `HTTP ${response.status}`;
      try { const body = await response.json(); if (body.error) msg = body.error; } catch {}
      throw new Error(msg);
    }
    return await response.json();
  },

  async updateArticleContent(articleId: string, content: string) {
    const response = await fetch(`${API_URL}/api/articles/${articleId}/content`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content }),
    });
    if (!response.ok) {
      let msg = `HTTP ${response.status}`;
      try { const b = await response.json(); if (b.error) msg = b.error; } catch {}
      throw new Error(msg);
    }
    return await response.json();
  },

  async checkHealth() {
    try {
      const response = await fetch(`${API_URL}/health`);
      return response.ok;
    } catch {
      return false;
    }
  },

  async getWorkflowStatus(workflowId: string) {
    const response = await fetch(`${API_URL}/api/workflow/${workflowId}`);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  },
};
